import React, { useEffect, useState, useRef } from "react";
import { ArrowLeft, ArrowUp } from "lucide-react";
import SectionJumpsNav from "./medical/SectionJumpsNav";

export default function FullScreenDetailModal({
  open,
  onClose,
  title,
  meta,
  image,
  sections = [],
  children,
}) {
  const [entered, setEntered] = useState(false);
  const scrollContainerRef = useRef(null);
  const hasSections = Array.isArray(sections) && sections.length > 0;
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  // Lock body scroll while modal is open
  useEffect(() => {
    if (!open) return undefined;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  // Slide-up entrance: start off-screen, then animate in after paint
  useEffect(() => {
    if (!open) {
      setEntered(false);
      return;
    }
    setEntered(false);
    const frame = requestAnimationFrame(() => {
      requestAnimationFrame(() => setEntered(true));
    });
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const scrollModalToTop = () => {
    scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40">
      <div
        className={`h-full w-full overflow-hidden bg-[#f8f8f4] shadow-2xl transition-transform duration-1000 ease-out ${
          entered ? "translate-y-0" : "translate-y-full"
        }`}
      >
        <div className="flex h-full flex-col relative">
          {hasSections && (
            <>
              {/* Desktop / tablet: always-visible sidebar on the right */}
              <div className="absolute right-0 top-1/2 z-10 hidden -translate-y-1/2 rounded-l-xl border border-gray-200/80 border-r-0 bg-white/95 shadow-[0_0_24px_rgba(0,0,0,0.08)] backdrop-blur-sm md:block">
                <SectionJumpsNav
                  key={open ? "modal-open" : "modal-closed"}
                  sections={sections}
                  scrollRootRef={scrollContainerRef}
                />
              </div>

              {/* Mobile: hidden sidebar, opened via + button just under the modal header */}
              {isMobileNavOpen && (
                <div className="fixed right-0 top-24 z-50 rounded-l-xl border border-gray-200/80 border-r-0 bg-white/95 shadow-[0_0_24px_rgba(0,0,0,0.12)] backdrop-blur-sm md:hidden">
                  <SectionJumpsNav
                    key={open ? "modal-open-mobile" : "modal-closed-mobile"}
                    sections={sections}
                    scrollRootRef={scrollContainerRef}
                    expanded
                  />
                </div>
              )}

              {/* Mobile toggle button: + / × to open/close section nav */}
              <button
                type="button"
                onClick={() => setIsMobileNavOpen((v) => !v)}
                className="fixed right-4 top-24 z-50 flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white/95 text-gray-900 shadow-md md:hidden"
                aria-label={
                  isMobileNavOpen
                    ? "Abschnittsnavigation schließen"
                    : "Abschnittsnavigation öffnen"
                }
              >
                <span className="text-2xl leading-none">
                  {isMobileNavOpen ? "×" : "+"}
                </span>
              </button>
            </>
          )}
          <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
            <div className="container-app pb-10 pt-8">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-4 text-md font-bold text-gray-800 shadow-sm "
              >
                <ArrowLeft className="h-[18px] w-[18px]" />
                <span>Zurück</span>
              </button>

              <div className="mx-auto w-full max-w-6xl">
                {meta && (
                  <div className="mt-6 text-xs font-semibold uppercase tracking-[0.16em] text-gray-600">
                    {meta}
                  </div>
                )}

                {title && (
                  <h2 className="mt-3 text-xl sm:text-3xl md:text-4xl font-semibold md:font-medium text-gray-900">
                    {title}
                  </h2>
                )}

                {image && (
                  <div className="mt-6 mb-8 h-56 overflow-hidden rounded-3xl md:h-64">
                    <img
                      src={image}
                      alt={title ? `Titelbild: ${title}` : "Detailansicht"}
                      className="h-full w-full object-cover"
                    />
                  </div>
                )}

                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
      <button
        type="button"
        onClick={scrollModalToTop}
        aria-label="Scroll to top"
        className="pointer-events-auto fixed bottom-6 right-6 z-[60] flex h-12 w-12 items-center justify-center rounded-full border-2 border-black bg-white text-black shadow-xl transition hover:bg-black hover:text-white focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-2"
      >
        <ArrowUp className="h-5 w-5" strokeWidth={2.5} />
      </button>
    </div>
  );
}

